'use client'

import React, { ReactNode, useState } from 'react'
import Image from 'next/image'
import { Image as ImageType, Size, Condition, Color } from '@/api'
import { imageUrl } from '@/lib/utils'
import Link from 'next/link'

type ListingItemProps = {
  uuid: string
  title: string
  listingPrice: number
  originalPrice?: number
  image?: ImageType
  images?: ImageType[]
  size?: Size
  condition?: Condition
  soldOutNode?: ReactNode
  color?: Color
  horizontal?: boolean
  sex?: string | null
  onClick?: () => void
  openInNewTab?: boolean
}

const getSexLabel = (sex: string | null | undefined) => {
  switch (sex) {
    case 'men':
      return 'Men'
    case 'women':
      return 'Women'
    case 'kids':
      return 'Kids'
    case 'unisex':
      return 'Unisex'
    default:
      return ''
  }
}

export const ListingItem: React.FC<ListingItemProps> = ({
  uuid,
  title,
  listingPrice,
  originalPrice,
  image,
  images,
  size,
  condition,
  soldOutNode,
  color,
  horizontal = false,
  sex,
  onClick,
  openInNewTab = false,
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const productImages =
    images && images.length > 0 ? images : image ? [image] : []
  const mainImage = productImages[0]
  const hoverImage = productImages[1]

  return (
    <Link
      href={`/listings/${uuid}`}
      onClick={onClick}
      target={openInNewTab ? '_blank' : undefined}
      rel={openInNewTab ? 'noopener noreferrer' : undefined}
    >
      <div
        className={`relative group cursor-pointer ${horizontal ? 'flex' : 'mb-5 md:mb-10'}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* 画像エリア */}
        <div
          className={`relative overflow-hidden ${
            horizontal
              ? 'w-20 sm:w-24 md:w-28 flex-shrink-0'
              : 'mb-2 sm:mb-3 w-full'
          }`}
        >
          <div className="relative w-full pb-[125%]">
            {mainImage && hoverImage ? (
              <>
                <Image
                  src={imageUrl(mainImage)}
                  alt={title}
                  fill
                  sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                  className={`object-cover transition-opacity duration-300 ${
                    isHovered ? 'opacity-0' : 'opacity-100'
                  }`}
                />
                <Image
                  src={imageUrl(hoverImage)}
                  alt={title}
                  fill
                  sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                  className={`object-cover transition-opacity duration-300 ${
                    isHovered ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                No Image
              </div>
            )}
          </div>
          {soldOutNode}
        </div>

        {/* 情報エリア */}
        <div className={`${horizontal ? 'w-2/3 pl-4' : 'w-full'} mt-2`}>
          <h3 className="text-xs sm:text-sm font-medium line-clamp-2 leading-[1.4] mb-1">
            {title}
          </h3>

          {/* ジェンダー | サイズ | コンディション | カラー */}
          <div className="flex items-center flex-wrap text-[10px] sm:text-[12px] text-[#727483] mb-1 min-h-[1.0rem] sm:min-h-[1.5rem]">
            {/* ジェンダー */}
            {sex && (
              <>
                <span>{getSexLabel(sex)}</span>
                {(size || condition || color) && (
                  <span className="mx-1">|</span>
                )}
              </>
            )}

            {/* サイズ */}
            {size && (
              <>
                <span>{size.name}</span>
                {(condition || color) && <span className="mx-1">|</span>}
              </>
            )}

            {/* コンディション */}
            {condition && (
              <>
                <span>{condition.name}</span>
                {color && <span className="mx-1">|</span>}
              </>
            )}

            {/* カラー */}
            {color && (
              <div className="flex items-center">
                <span>{color.name}</span>
              </div>
            )}
          </div>

          {/* 価格 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
            <div className="text-xs sm:text-sm text-black">
              <span>¥{listingPrice.toLocaleString()}</span>
              <span className="ml-1">(tax in)</span>
            </div>

            {/* 元の小売価格（税込: 1.1倍） */}
            {originalPrice && (
              <div className="text-[10px] sm:text-xs text-[#727483]">
                ¥{Math.round(originalPrice * 1.1).toLocaleString()} (新品時の参考価格)
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  )
}
